import type { PayloadAction } from "@reduxjs/toolkit"
import { createSlice } from "@reduxjs/toolkit"

import { PayerCoverageCheckNetwork } from "@common-shared/config/coverage.config"
import { QuotaUsageType } from "@common-shared/config/oneVerify/oneVerifyCoverage.config.ts"
import { PolicyModalityCode } from "@common-shared/config/oneVerify/oneVerifyPolicies.config.ts"
import { IOneVerifyEhrWriteStatusUpdatedSocketsPayload } from "@common-shared/types/object.types.ts"
import {
    IOneVerifyCoverageCheckHistory,
    IOneVerifyPolicy,
} from "@common-shared/types/responseData/oneVerifyResponseData.types.ts"

import {
    CoverageCheckerMode,
    CoverageCheckerRunningState,
} from "../../config/checker.config"
import type { IDynamicCheckerFormInputData } from "../../types/data.types.ts"
import type { ICheckerState } from "../../types/state.types.ts"

const initialState: ICheckerState = {
    checkerMode: CoverageCheckerMode.MANUAL,
    manualCheckType: undefined,
    modalityCode: undefined,
    csvCheckType: QuotaUsageType.GENERAL,
    runningState: CoverageCheckerRunningState.INPUT_MANUAL,
    coverageChecksSubmitted: false,
    isCheckTypeRelatedDataEntered: false,
    inputDataSetAt: new Date().getTime(),
    continuousMonitoringCoverageChecks: [],
    medicaidChecks: [],
    bulkCheckerRunId: null,
}

const checkerSlice = createSlice({
    name: "checker",
    initialState,
    reducers: {
        checkerReset: state => {
            return {
                ...initialState,

                // Preserve is csv running type during reset
                checkerMode: state.checkerMode,
                manualCheckType: state.manualCheckType,
                csvCheckType: state.csvCheckType,
                runningState:
                    state.checkerMode === CoverageCheckerMode.CSV
                        ? CoverageCheckerRunningState.INPUT_CSV
                        : CoverageCheckerRunningState.INPUT_MANUAL,
                modalityCode: state.modalityCode,
            }
        },

        checkerResetWithKeepingInputData: state => {
            return {
                ...initialState,

                // Preserve is csv running type during reset
                checkerMode: state.checkerMode,
                manualCheckType: state.manualCheckType,
                csvCheckType: state.csvCheckType,
                runningState:
                    state.checkerMode === CoverageCheckerMode.CSV
                        ? CoverageCheckerRunningState.INPUT_CSV
                        : CoverageCheckerRunningState.INPUT_MANUAL,

                // Preserve continuous monitoring input data
                continuousMonitoringCoverageChecks:
                    state.continuousMonitoringCoverageChecks.map(
                        ({ inputData }) => {
                            return {
                                inputData,
                                result: null,
                            }
                        },
                    ),

                // Preserve medicaid input data
                medicaidChecks: state.medicaidChecks.map(({ inputData }) => {
                    return {
                        inputData,
                        result: null,
                    }
                }),
            }
        },

        checkerToggleRunningType: (
            state,
            action: PayloadAction<{ checkerMode?: CoverageCheckerMode }>,
        ) => {
            // Toggle is csv running type + reset
            // Can accept specific value or will toggle

            const newCheckerMode =
                typeof action.payload.checkerMode !== "undefined"
                    ? action.payload.checkerMode
                    : state.checkerMode === CoverageCheckerMode.CSV
                      ? CoverageCheckerMode.MANUAL
                      : CoverageCheckerMode.CSV

            return {
                ...initialState,

                checkerMode: newCheckerMode,
                manualCheckType: state.manualCheckType,
                csvCheckType: state.csvCheckType,
                runningState:
                    newCheckerMode === CoverageCheckerMode.CSV
                        ? CoverageCheckerRunningState.INPUT_CSV
                        : CoverageCheckerRunningState.INPUT_MANUAL,
            }
        },

        checkerSetManualCheckType: (
            state,
            action: PayloadAction<QuotaUsageType | undefined>,
        ) => {
            state.manualCheckType = action.payload
        },

        checkerSetCsvCheckType: (
            state,
            action: PayloadAction<QuotaUsageType>,
        ) => {
            state.csvCheckType = action.payload
        },

        checkerSetRunningState: (
            state,
            action: PayloadAction<CoverageCheckerRunningState>,
        ) => {
            state.runningState = action.payload
        },

        checkerSetCoverageChecksSubmitted: (
            state,
            action: PayloadAction<boolean>,
        ) => {
            state.coverageChecksSubmitted = action.payload
        },

        checkerSetBulkCheckerRunId: (
            state,
            action: PayloadAction<string | null>,
        ) => {
            state.bulkCheckerRunId = action.payload
        },

        checkerUpdateInputDataZipCodes: (
            state,
            action: PayloadAction<Record<string, string>>,
        ) => {
            const checks =
                state.csvCheckType === QuotaUsageType.MEDICAID
                    ? state.medicaidChecks
                    : state.continuousMonitoringCoverageChecks

            checks.forEach(check => {
                const zipCode = action.payload[check.inputData.id]

                if (zipCode !== undefined) {
                    check.inputData.zipCode = zipCode
                }
            })
        },

        checkerSetIsCheckTypeRelatedDataEntered: (
            state,
            action: PayloadAction<boolean>,
        ) => {
            state.isCheckTypeRelatedDataEntered = action.payload
        },

        checkerSetModalityCode: (
            state,
            action: PayloadAction<PolicyModalityCode | undefined>,
        ) => {
            state.modalityCode = action.payload
        },

        checkerSetContinuousMonitoringInputData: (
            state,
            action: PayloadAction<IDynamicCheckerFormInputData[]>,
        ) => {
            state.inputDataSetAt = new Date().getTime()

            state.continuousMonitoringCoverageChecks = action.payload.map(
                inputData => ({
                    inputData,
                    result: null,
                }),
            )

            state.medicaidChecks = []
        },

        checkerRemoveContinuousMonitoringInputData: (
            state,
            action: PayloadAction<IDynamicCheckerFormInputData>,
        ) => {
            state.inputDataSetAt = new Date().getTime()

            state.continuousMonitoringCoverageChecks =
                state.continuousMonitoringCoverageChecks.filter(
                    item => item.inputData.id !== action.payload.id,
                )
        },

        checkerReceivedContinuousMonitoringCoverageResult: (
            state,
            action: PayloadAction<IOneVerifyCoverageCheckHistory>,
        ) => {
            const newCoverageChecks = [
                ...state.continuousMonitoringCoverageChecks,
            ]

            // Use input data id that we've generated previously -> find needed index by it
            const neededCheckIndex = newCoverageChecks.findIndex(
                check =>
                    check.inputData.id ===
                    action.payload.coverageResult.resultId,
            )

            if (~neededCheckIndex) {
                newCoverageChecks[neededCheckIndex].result = {
                    ...(newCoverageChecks[neededCheckIndex].result || {}),

                    [action.payload.coverageResult.isInNetworkCheck
                        ? PayerCoverageCheckNetwork.IN
                        : PayerCoverageCheckNetwork.OUT]: action.payload,
                }
            }

            state.continuousMonitoringCoverageChecks = newCoverageChecks
        },

        checkerSetMedicaidInputData: (
            state,
            action: PayloadAction<IDynamicCheckerFormInputData[]>,
        ) => {
            state.inputDataSetAt = new Date().getTime()

            state.medicaidChecks = action.payload.map(inputData => ({
                inputData,
                result: null,
            }))

            state.continuousMonitoringCoverageChecks = []
        },

        checkerReceivedMedicaidCoverageResult: (
            state,
            action: PayloadAction<IOneVerifyCoverageCheckHistory>,
        ) => {
            const newCoverageChecks = [...state.medicaidChecks]

            // Use input data id that we've generated previously -> find needed index by it
            const neededCheckIndex = newCoverageChecks.findIndex(
                check =>
                    check.inputData.id ===
                    action.payload.coverageResult.resultId,
            )

            if (~neededCheckIndex) {
                newCoverageChecks[neededCheckIndex].result = action.payload
            }

            state.medicaidChecks = newCoverageChecks
        },

        checkerRemoveMedicaidInputData: (
            state,
            action: PayloadAction<IDynamicCheckerFormInputData>,
        ) => {
            state.inputDataSetAt = new Date().getTime()

            state.medicaidChecks = state.medicaidChecks.filter(
                item => item.inputData.id !== action.payload.id,
            )
        },

        checkerUpdatedResultEhrWriteStatus: (
            state,
            action: PayloadAction<IOneVerifyEhrWriteStatusUpdatedSocketsPayload>,
        ) => {
            function processEhrWriteLog(policy?: IOneVerifyPolicy | null) {
                if (policy?.id === action.payload.policyId) {
                    policy.ehrWriteLog = action.payload.ehrWriteLog
                }
            }

            state.continuousMonitoringCoverageChecks.forEach(check => {
                ;(
                    [
                        PayerCoverageCheckNetwork.IN,
                        PayerCoverageCheckNetwork.OUT,
                    ] as const
                ).forEach(network => {
                    processEhrWriteLog(check.result?.[network]?.policy)
                })
            })

            state.medicaidChecks.forEach(check => {
                processEhrWriteLog(check.result?.policy)
            })
        },
    },
})

export const {
    checkerReset,
    checkerResetWithKeepingInputData,
    checkerToggleRunningType,
    checkerSetRunningState,
    checkerSetModalityCode,
    checkerSetManualCheckType,
    checkerSetCsvCheckType,
    checkerSetContinuousMonitoringInputData,
    checkerRemoveContinuousMonitoringInputData,
    checkerSetCoverageChecksSubmitted,
    checkerSetBulkCheckerRunId,
    checkerUpdateInputDataZipCodes,
    checkerSetIsCheckTypeRelatedDataEntered,
    checkerReceivedContinuousMonitoringCoverageResult,
    checkerSetMedicaidInputData,
    checkerReceivedMedicaidCoverageResult,
    checkerRemoveMedicaidInputData,
    checkerUpdatedResultEhrWriteStatus,
} = checkerSlice.actions

export const checkerReducer = checkerSlice.reducer
